import { GoogleMapsModule } from '@angular/google-maps';
import enDateLocale from 'date-fns/locale/en-US/index';
import { en_US, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  Provider,
  NO_ERRORS_SCHEMA,
  APP_INITIALIZER,
} from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { createHttpLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
// libs
import { environment } from '@macs-frontend/env';
import { ApiHeadersInterceptor } from '@macs-frontend/web-macs/core/api-headers.interceptor';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/browser';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { NgrxSelect } from 'macs-ngrx-actions';
import { provideNgxMask } from 'ngx-mask';
import { AuthModule } from './auth/auth.module';
import { possibleTypes } from './graphql-possible-types';
import { ErrorInterceptor } from './auth/error.Interceptor';
import { KeycloakService } from './auth/services/keycloak.service';
import { TokenInterceptor } from './auth/token.interceptor';
import { CompanyHeaderInterceptor } from './auth/company-header.interceptor';
import { AppComponent } from './core/containers/app/app.component';
// app
import { CoreModule } from './core/core.module';
import { NotificationStore } from './core/store/notification.store';
import { LoaderEffects } from './effects/loader.effects';
import { MetaEffects } from './effects/meta.effects';
import { SharedModule } from './features/shared/shared.module';
import { metaReducers, reducers } from './reducers';
import { routes } from './routes';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

export function getBaseHref(platformLocation: PlatformLocation): string {
  return platformLocation.getBaseHrefFromDOM();
}

export function keycloakInitializer(keycloak: KeycloakService): () => Promise<void> {
  return () => keycloak.init().then(() => undefined);
}

export function initGoogleMapsApi(): () => Promise<void> {
  return () =>
    new Promise<void>((resolve, reject) => {
      console.log('initGoogleMapsApi: Starting Google Maps initialization');

      // Check if already loaded
      if (typeof google !== 'undefined' && google.maps) {
        console.log('initGoogleMapsApi: Google Maps already loaded');
        resolve();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        console.log(
          'initGoogleMapsApi: Google Maps script already exists, waiting for load',
        );
        existingScript.addEventListener('load', () => {
          console.log('initGoogleMapsApi: Existing script loaded');
          setTimeout(() => {
            console.log('initGoogleMapsApi: Google Maps ready after timeout');
            resolve();
          }, 100);
        });
        existingScript.addEventListener('error', e => {
          console.error('initGoogleMapsApi: Error loading existing script', e);
          resolve(); // Still resolve to not block app
        });
        return;
      }

      console.log('initGoogleMapsApi: Creating new script tag');
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.maps_key}&libraries=places`;
      console.log('initGoogleMapsApi: Script URL:', script.src);

      script.onload = () => {
        console.log('initGoogleMapsApi: Script loaded, waiting 100ms');
        setTimeout(() => {
          console.log('initGoogleMapsApi: Checking if google.maps is available');
          console.log('initGoogleMapsApi: typeof google =', typeof google);
          console.log(
            'initGoogleMapsApi: typeof google.maps =',
            typeof (window as any).google?.maps,
          );
          resolve();
        }, 100);
      };
      script.onerror = e => {
        console.error('initGoogleMapsApi: Failed to load Google Maps API', e);
        resolve(); // Still resolve to not block app
      };

      console.log('initGoogleMapsApi: Appending script to head');
      document.head.appendChild(script);
    });
}

const providers: Provider[] = [
  Apollo,
  {
    provide: APP_INITIALIZER,
    useFactory: keycloakInitializer,
    deps: [KeycloakService],
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    useFactory: initGoogleMapsApi,
    multi: true,
  },
  {
    provide: APP_BASE_HREF,
    useFactory: getBaseHref,
    deps: [PlatformLocation],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: TokenInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: CompanyHeaderInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true,
  },
  NgrxSelect,
  {
    provide: APOLLO_OPTIONS,
    useFactory: (notificationStore: NotificationStore, keycloak: KeycloakService) => {
      const handleErrorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          notificationStore.showApolloErrorsWithMacrotask(graphQLErrors);
        }

        if (networkError) {
          notificationStore.showApolloNetworkError(networkError);
        }
      });

      const httpLink = createHttpLink({
        uri: environment.graphql_uri,
        useGETForQueries: true,
      });

      const middlewareLink = setContext(async (_operation, prevContext) => {
        const token = keycloak.enabled
          ? await keycloak.getToken()
          : localStorage.getItem('token') ?? '';
        if (!token) return prevContext;
        return {
          ...prevContext,
          headers: {
            ...(prevContext['headers'] ?? {}),
            Authorization: `Bearer ${token}`,
          },
        };
      });

      return {
        cache: new InMemoryCache({
          dataIdFromObject: obj => (obj as any).uuid || (obj as any).hash,
          possibleTypes,
        }),
        link: middlewareLink.concat(handleErrorLink).concat(httpLink),
      };
    },
    deps: [NotificationStore, KeycloakService],
  },
  ...provideNgxMask(),
];

if (!environment.production) {
  providers.push({
    provide: HTTP_INTERCEPTORS,
    useClass: ApiHeadersInterceptor,
    multi: true,
  });
}

// environment.server speaks the app's own vocabulary -- dev / stage-branch /
// stage-mirror / pre-live / live -- which is useful but is not what every other
// reporter sends. The Java services and Symfony report dev / staging / prod, so
// handing GlitchTip environment.server verbatim would leave one environment
// dropdown holding six spellings of three environments and no filter that
// selects "staging" and gets it. The exact server keeps its own tag below, so
// nothing is lost by normalising the environment itself.
function glitchtipEnvironment(): 'dev' | 'staging' | 'prod' {
  switch (environment.server) {
    case 'live':
      return 'prod';
    case 'stage-branch':
    case 'stage-mirror':
    case 'pre-live':
      return 'staging';
    default:
      return 'dev';
  }
}

if (environment.sentry_dsn) {
  Sentry.init({
    dsn: environment.sentry_dsn,
    release: environment.release,
    environment: glitchtipEnvironment(),
    // Errors go to self-hosted GlitchTip, not sentry.io. GlitchTip implements
    // the Sentry ingest API deliberately and ships no SDK of its own, so the
    // client stays @sentry/browser — only the DSN decides the destination.
    //
    // No browserTracingIntegration(): GlitchTip does not ingest transactions,
    // so tracing would only cost bandwidth on envelopes discarded on arrival.
    integrations: [],
    tracesSampleRate: 0,
  });

  Sentry.setTags({
    branch: environment.branch,
    server: environment.server,
    revision: environment.revision,
    release_url: environment.release_url,
  });

  class SentryErrorHandler implements ErrorHandler {
    constructor() {}

    handleError(error: any) {
      if (error === 'Invalid time') {
        Sentry.captureMessage(error, 'log');
        // https://github.com/primefaces/primeng/blob/master/src/app/components/calendar/calendar.ts#L2230
        // Componnent <p-calendar /> throws this error when incomplete time entered.
        // In team schedule - user should be able to enter incomplete time.
        return;
      }

      // No Sentry.showReportDialog() any more: it posts to Sentry's embedded
      // error-page endpoint, which GlitchTip does not implement. Nothing needs
      // the returned event id, so it is not captured.
      Sentry.captureException(
        error.originalError || error.error?.error || error.error || error,
      );
    }
  }

  providers.push({
    provide: ErrorHandler,
    useClass: SentryErrorHandler,
  });
}

@NgModule({
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA],
  imports: [
    CoreModule,
    BrowserAnimationsModule,
    SharedModule,
    RouterModule.forRoot(routes),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        // Disabled to allow existing mutation patterns in reducers
        // TODO: Refactor all reducers to return new state objects, then re-enable
        strictStateImmutability: false,
        strictActionImmutability: false,
        // strictStateSerializability: true,
        // strictActionSerializability: true,
        // strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
    }),
    StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
    EffectsModule.forRoot([LoaderEffects, MetaEffects]),
    AuthModule,
    GoogleMapsModule,
    // Keep redux-devtools instrumentation out of the production bundle entirely
    // (Angular flattens the empty array in imports). Dev/staging still get it.
    environment.production ? [] : StoreDevtoolsModule.instrument({ maxAge: 25 }),
    MatSnackBarModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  providers: [
    ...providers,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'assets/tinymce/tinymce.min.js' },
    // Without these, ng-zorro falls back to its zh_CN default and every nz
    // date picker renders in Chinese (dropped at fork time from macs-clone).
    { provide: NZ_I18N, useValue: en_US },
    { provide: NZ_DATE_LOCALE, useValue: enDateLocale },
  ],
})
export class AppModule {}
